import { lazy, Suspense } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { createBrowserRouter } from 'react-router-dom';

// Guest scan — loaded eagerly (marketing entry point, frequently visited)
import GuestScan from './routes/scan/GuestScan';

// Auth pages — loaded eagerly (small, always needed)
import Login from './routes/auth/Login';
import Signup from './routes/auth/Signup';
import Verify from './routes/auth/Verify';
import ForgotPassword from './routes/auth/ForgotPassword';
import ResetPassword from './routes/auth/ResetPassword';
import OAuthCallback from './routes/auth/OAuthCallback';

// App shell — loaded eagerly (auth guard lives here)
import AppLayout from './routes/app/AppLayout';
import Dashboard from './routes/app/Dashboard';

// Phase 6 — Resume builder (lazy-loaded; heavy Tiptap + dnd-kit deps)
const ResumeList = lazy(() => import('./routes/app/resume/ResumeList'));
const ResumeBuilder = lazy(() => import('./routes/app/resume/ResumeBuilder'));

// Phase 6 — Public share view (lazy-loaded; no auth required)
const ShareView = lazy(() => import('./routes/share/ShareView'));

// Phase 7 — AI Tools hub
const AIToolsPage = lazy(() => import('./routes/app/tools/AIToolsPage'));

// Phase 9 — Billing
const Billing = lazy(() => import('./routes/app/billing/Billing'));
const CheckoutSuccess = lazy(() => import('./routes/app/billing/CheckoutSuccess'));

// Phase 10 — Folio
const FolioPage = lazy(() => import('./routes/app/folio/FolioPage'));
const FolioSetup = lazy(() => import('./routes/app/folio/FolioSetup'));
const SettingsPlaceholder = lazy(() =>
  import('./routes/app/AppLayout').then((_m) => ({ default: () => <ComingSoon page="Settings" /> })),
);

function PageLoader() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ComingSoon({ page }: { page: string }) {
  return (
    <div className="p-8">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">{page}</h1>
      <p className="text-sm text-gray-500">Coming in a later phase.</p>
    </div>
  );
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  // ── Guest scan (unauthenticated — marketing entry point) ───────────────
  { path: '/scan', element: <GuestScan /> },

  // ── Public auth routes ──────────────────────────────────────────────────
  { path: '/login', element: <Login /> },
  { path: '/signup', element: <Signup /> },
  { path: '/verify', element: <Verify /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/oauth/callback', element: <OAuthCallback /> },

  // ── Phase 6: Public shared resume view (no auth required) ──────────────
  {
    path: '/share/:token',
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <ShareView />
        </Suspense>
      </ErrorBoundary>
    ),
  },

  // ── Phase 9: Checkout success (outside AppLayout — no sidebar needed) ────
  {
    path: '/billing/success',
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <CheckoutSuccess />
        </Suspense>
      </ErrorBoundary>
    ),
  },

  // ── Authenticated routes (wrapped by AppLayout auth guard) ──────────────
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <Dashboard /> },
      {
        path: 'resume',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <ResumeList />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'resume/:resumeId',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <ResumeBuilder />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'tools',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <AIToolsPage />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'billing',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Billing />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'folio',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <FolioPage />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'folio/setup',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <FolioSetup />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'settings',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <SettingsPlaceholder />
            </Suspense>
          </ErrorBoundary>
        ),
      },
    ],
  },
]);
